import { msg } from "../../locales/translate.ts";
export interface CursorPage<Row> {
	rows: Row[];
	/** null explicitly marks the last page. A missing cursor is not end-of-data. */
	nextCursor: string | null;
	total?: number;
}

export interface ExportProgress {
	phase: "loading" | "preparing";
	loaded: number;
	pages: number;
	total?: number;
}

export interface CursorExportJob<Row> {
	filename: string;
	loadPage: (cursor: string | undefined, signal: AbortSignal) => Promise<CursorPage<Row>>;
	buildFile: (rows: Row[], signal: AbortSignal) => Blob | Promise<Blob>;
	getRowKey?: (row: Row) => string | number;
	/** Recheck permissions/context immediately before the file can be saved. */
	beforeSave?: () => void | Promise<void>;
}

/** Fetch in order, keep every row, and never produce a file from a partial result. */
export async function runCursorExport<Row>(job: CursorExportJob<Row>, signal: AbortSignal, onProgress: (progress: ExportProgress) => void) {
	const rows: Row[] = [];
	const cursors = new Set<string>();
	const rowKeys = new Set<string | number>();
	let cursor: string | undefined;
	let total: number | undefined;
	let pages = 0;
	signal.throwIfAborted();
	onProgress({ phase: "loading", loaded: 0, pages: 0 });
	while (true) {
		signal.throwIfAborted();
		const chunk = await job.loadPage(cursor, signal);
		signal.throwIfAborted();
		if (!chunk || !Array.isArray(chunk.rows) || (chunk.nextCursor !== null && (typeof chunk.nextCursor !== "string" || !chunk.nextCursor.trim()))) {
			throw new Error(msg("paginationInformationIsMissingOrInvalidTheFileWasNotSaved"));
		}
		if (chunk.nextCursor !== null && cursors.has(chunk.nextCursor)) throw new Error(msg("theServerRepeatedAPagePleaseRestartTheExport"));
		if (chunk.total !== undefined) {
			if (!Number.isSafeInteger(chunk.total) || chunk.total < 0) throw new Error(msg("theServerReturnedAnInvalidTotal"));
			if (total !== undefined && total !== chunk.total) throw new Error(msg("theDataChangedDuringExportPleaseRestartTheExport"));
			total = chunk.total;
		}
		for (const row of chunk.rows) {
			if (job.getRowKey) {
				const key = job.getRowKey(row);
				if (rowKeys.has(key)) throw new Error(msg("theServerRepeatedARecordPleaseRestartTheExport"));
				rowKeys.add(key);
			}
			rows.push(row);
		}
		pages++;
		if (total !== undefined && rows.length > total) throw new Error(msg("theExportExceedsTheReportedTotalPleaseRestartTheExport"));
		onProgress({ phase: "loading", loaded: rows.length, pages, total });
		if (chunk.nextCursor === null) {
			if (total !== undefined && rows.length !== total) throw new Error(msg("someRecordsAreMissingTheFileWasNotSaved"));
			break;
		}
		cursor = chunk.nextCursor;
		cursors.add(cursor);
	}
	signal.throwIfAborted();
	onProgress({ phase: "preparing", loaded: rows.length, pages, total });
	// Give the progress dialog a chance to paint before formatting the collected rows.
	await new Promise<void>(resolve => setTimeout(resolve, 0));
	signal.throwIfAborted();
	const file = await job.buildFile(rows, signal);
	signal.throwIfAborted();
	await job.beforeSave?.();
	signal.throwIfAborted();
	return file;
}
